/**
 * Header/magic-byte file-type detection for the pricelist upload flow.
 *
 * Detection is driven by the file's leading bytes (and, for ZIP-based Office
 * documents, by the entry names embedded in the archive) rather than by the
 * file extension or a client-supplied MIME type. Both the frontend (validating
 * a selected file before upload) and the backend (routing an uploaded buffer to
 * the correct extraction handler) share this logic so the two agree on what a
 * file is.
 */

export type PricelistFileKind = "pdf" | "excel" | "image" | "csv";

export interface DetectedFileType {
  kind: PricelistFileKind;
  mimeType: string;
}

/** MIME types accepted for the "image" kind, keyed for reuse in UI copy. */
export const SUPPORTED_IMAGE_MIME_TYPES = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "image/bmp",
  "image/svg+xml",
] as const;

export const PDF_MIME_TYPE = "application/pdf";
export const XLSX_MIME_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
export const XLS_MIME_TYPE = "application/vnd.ms-excel";
/** Delimited text of any flavour — a `.txt` of tab-separated rows is this too. */
export const CSV_MIME_TYPE = "text/csv";

/**
 * Every MIME type that can be the source file of a pricelist.
 *
 * `text/csv` is both a source type and the type of every *extracted* CSV, so
 * MIME type alone no longer tells the two apart. The discriminator is
 * `File.parentFileId`: a source file has no parent, an extracted CSV always
 * records the source as its parent. Queries that mean "source pricelists" must
 * filter on that, not on this list.
 */
export const SUPPORTED_PRICELIST_SOURCE_MIME_TYPES = [
  PDF_MIME_TYPE,
  XLSX_MIME_TYPE,
  XLS_MIME_TYPE,
  CSV_MIME_TYPE,
  ...SUPPORTED_IMAGE_MIME_TYPES,
] as const;

/**
 * Upload ceiling per pricelist file kind, shared by the frontend validator and
 * the backend's post-upload check so both enforce the same number.
 *
 * The split reflects what each format costs to extract: delimited text and
 * spreadsheets stream through the schema-map engine without a model call per
 * row, so their ceiling is the GEN-2321 target. PDFs and images still cost one
 * model call per page batch / image band, so their caps are a cost and latency
 * decision, not a technical one.
 */
export const PRICELIST_UPLOAD_MAX_BYTES: Record<PricelistFileKind, number> = {
  csv: 1024 * 1024 * 1024, // 1 GB
  excel: 1024 * 1024 * 1024, // 1 GB
  pdf: 100 * 1024 * 1024, // 100 MB
  image: 25 * 1024 * 1024, // 25 MB
};

/** The largest cap of any kind — the presign-time gate, before bytes exist to detect a kind from. */
export const PRICELIST_UPLOAD_MAX_BYTES_ANY_KIND = Math.max(
  ...Object.values(PRICELIST_UPLOAD_MAX_BYTES),
);

/**
 * How many leading bytes {@link detectFileTypeFromSample} needs. Covers every
 * magic number, the SVG scan window, and the delimited-text sample.
 */
export const PRICELIST_DETECTION_SAMPLE_BYTES = 64 * 1024;

function startsWith(bytes: Uint8Array, sig: number[], offset = 0): boolean {
  if (bytes.length < offset + sig.length) return false;
  for (let i = 0; i < sig.length; i++) {
    if (bytes[offset + i] !== sig[i]) return false;
  }
  return true;
}

/** Locate an ASCII substring within a byte array. Returns true if present. */
function containsAscii(
  bytes: Uint8Array,
  needle: string,
  limit?: number,
): boolean {
  const end = limit != null ? Math.min(bytes.length, limit) : bytes.length;
  const first = needle.charCodeAt(0);
  outer: for (let i = 0; i <= end - needle.length; i++) {
    if (bytes[i] !== first) continue;
    for (let j = 1; j < needle.length; j++) {
      if (bytes[i + j] !== needle.charCodeAt(j)) continue outer;
    }
    return true;
  }
  return false;
}

// ZIP archives store each entry's path as plaintext in its local file header,
// so an xlsx/xlsm/xlsb workbook can be recognised by the "xl/workbook" entry
// without decompressing anything. This also rejects other ZIP-based Office
// formats (docx → "word/", pptx → "ppt/").
function zipIsExcelWorkbook(bytes: Uint8Array): boolean {
  return containsAscii(bytes, "xl/workbook");
}

// SVG has no binary magic number. Scan the leading bytes (skipping a UTF-8 BOM)
// for an "<svg" root element, which may follow an XML declaration, comments, or
// a DOCTYPE.
function looksLikeSvg(bytes: Uint8Array): boolean {
  const hasBom = startsWith(bytes, [0xef, 0xbb, 0xbf]);
  const scanStart = hasBom ? 3 : 0;
  const window = bytes.subarray(scanStart, scanStart + 2048);
  return containsAscii(window, "<svg") || containsAscii(window, "<SVG");
}

/** How much of the file the delimited-text check reads. */
const TEXT_SAMPLE_BYTES = 8192;

/** Candidate CSV delimiters, checked most-common first. */
const TEXT_DELIMITERS = [",", "\t", ";", "|"] as const;

const UTF8_BOM = [0xef, 0xbb, 0xbf];

function isWhitespaceByte(byte: number): boolean {
  return byte === 0x20 || byte === 0x09 || byte === 0x0a || byte === 0x0d;
}

/**
 * Binary guard: a NUL is disqualifying outright, and more than 1% other
 * control characters (allowing tab/LF/CR) means this is not text.
 */
function isBinaryLike(sample: Uint8Array): boolean {
  let controlChars = 0;
  for (const byte of sample) {
    if (byte === 0) return true;
    if (byte < 0x20 && byte !== 0x09 && byte !== 0x0a && byte !== 0x0d) {
      controlChars++;
    }
  }
  return controlChars / sample.length > 0.01;
}

/**
 * Text, but a document rather than a table: JSON and markup both contain
 * plenty of commas and would otherwise pass the delimiter test. Judged by the
 * first meaningful byte being `{`, `[` or `<` — skipping a UTF-8 BOM first,
 * which would otherwise stop the scan before that byte is ever seen.
 */
function startsStructuredDocument(sample: Uint8Array): boolean {
  const start = startsWith(sample, UTF8_BOM) ? UTF8_BOM.length : 0;
  for (const byte of sample.subarray(start)) {
    if (!isWhitespaceByte(byte)) {
      return byte === 0x7b || byte === 0x5b || byte === 0x3c; // { [ <
    }
  }
  return false;
}

/**
 * How many non-blank lines the sample holds, and how many of them carry each
 * candidate delimiter. Computed on raw bytes: the delimiters and newlines are
 * all ASCII, and UTF-8 multibyte sequences never contain ASCII values, so no
 * decoding is needed.
 *
 * When the sample window is full, the trailing line is likely cut mid-row and
 * says nothing about the file's shape — it only counts on a short read.
 */
function delimiterLineStats(sample: Uint8Array): {
  lineCount: number;
  linesWithDelimiter: number[];
} {
  const delimiterBytes = TEXT_DELIMITERS.map((d) => d.charCodeAt(0));
  const linesWithDelimiter = delimiterBytes.map(() => 0);
  const seenOnLine = delimiterBytes.map(() => false);
  let lineCount = 0;
  let lineHasContent = false;

  const endLine = () => {
    if (lineHasContent) {
      lineCount++;
      for (let i = 0; i < delimiterBytes.length; i++) {
        if (seenOnLine[i]) linesWithDelimiter[i]++;
      }
    }
    seenOnLine.fill(false);
    lineHasContent = false;
  };

  for (const byte of sample) {
    if (byte === 0x0a || byte === 0x0d) {
      endLine();
      continue;
    }
    if (byte !== 0x20 && byte !== 0x09) lineHasContent = true;
    const index = delimiterBytes.indexOf(byte);
    if (index !== -1) seenOnLine[index] = true;
  }
  if (sample.length < TEXT_SAMPLE_BYTES) endLine();

  return { lineCount, linesWithDelimiter };
}

/**
 * Whether the leading bytes read as delimited text — a CSV, or a `.txt` of
 * tab/semicolon/pipe-separated rows.
 *
 * Delimited text is the one supported format with no magic number, so unlike
 * everything else in this file it has to be recognised by what the content
 * looks like rather than what it starts with. The bar: printable text, not a
 * JSON/markup document, at least two lines, and one delimiter that appears on
 * most of them. "Most" rather than all, because real pricelist exports open
 * with banner lines ("ACME PRICE LIST 2026") that carry no delimiter.
 *
 * A single-column file fails this check on purpose: with no delimiter anywhere
 * it is indistinguishable from prose, and a one-column pricelist has nothing
 * to extract.
 */
function looksLikeDelimitedText(bytes: Uint8Array): boolean {
  const sample = bytes.subarray(0, TEXT_SAMPLE_BYTES);
  if (sample.length === 0) return false;
  if (isBinaryLike(sample)) return false;
  if (startsStructuredDocument(sample)) return false;

  const { lineCount, linesWithDelimiter } = delimiterLineStats(sample);
  if (lineCount < 2) return false;
  return linesWithDelimiter.some(
    (count) => count >= Math.ceil(lineCount * 0.6),
  );
}

/**
 * Detect a pricelist file's kind and canonical MIME type from its raw bytes.
 * Returns null when the bytes match no supported type.
 */
export function detectFileType(bytes: Uint8Array): DetectedFileType | null {
  // PDF — "%PDF"
  if (startsWith(bytes, [0x25, 0x50, 0x44, 0x46])) {
    return { kind: "pdf", mimeType: PDF_MIME_TYPE };
  }
  // PNG
  if (startsWith(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) {
    return { kind: "image", mimeType: "image/png" };
  }
  // JPEG
  if (startsWith(bytes, [0xff, 0xd8, 0xff])) {
    return { kind: "image", mimeType: "image/jpeg" };
  }
  // GIF — "GIF8"
  if (startsWith(bytes, [0x47, 0x49, 0x46, 0x38])) {
    return { kind: "image", mimeType: "image/gif" };
  }
  // BMP — "BM"
  if (startsWith(bytes, [0x42, 0x4d])) {
    return { kind: "image", mimeType: "image/bmp" };
  }
  // WEBP — "RIFF"????"WEBP"
  if (
    startsWith(bytes, [0x52, 0x49, 0x46, 0x46]) &&
    startsWith(bytes, [0x57, 0x45, 0x42, 0x50], 8)
  ) {
    return { kind: "image", mimeType: "image/webp" };
  }
  // Legacy XLS — OLE2 compound file
  if (startsWith(bytes, [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1])) {
    return { kind: "excel", mimeType: XLS_MIME_TYPE };
  }
  // ZIP container — xlsx/xlsm/xlsb (also docx/pptx/plain zip, which we reject)
  if (
    startsWith(bytes, [0x50, 0x4b, 0x03, 0x04]) ||
    startsWith(bytes, [0x50, 0x4b, 0x05, 0x06]) ||
    startsWith(bytes, [0x50, 0x4b, 0x07, 0x08])
  ) {
    return zipIsExcelWorkbook(bytes)
      ? { kind: "excel", mimeType: XLSX_MIME_TYPE }
      : null;
  }
  // SVG — text-based, so it must be checked before the delimited-text
  // fallback claims it.
  if (looksLikeSvg(bytes)) {
    return { kind: "image", mimeType: "image/svg+xml" };
  }
  // Delimited text — the least specific signature, so always last.
  if (looksLikeDelimitedText(bytes)) {
    return { kind: "csv", mimeType: CSV_MIME_TYPE };
  }
  return null;
}

/**
 * Detect a file's kind from its first {@link PRICELIST_DETECTION_SAMPLE_BYTES}
 * bytes, for callers that cannot afford to read the whole file (a 1 GB blob in
 * storage, a large File in the browser).
 *
 * Differs from {@link detectFileType} in exactly one case: a ZIP whose
 * `xl/workbook` entry sits beyond the sample. SheetJS and other exporters
 * write worksheet entries first, so on a big workbook the proof of Excel-ness
 * may be megabytes in — indistinguishable from a non-Excel ZIP without reading
 * to the central directory. Such a ZIP is accepted as XLSX here; the extraction
 * engine rejects non-workbook archives loudly on first read, which is a later
 * but clearer failure than refusing a legitimate spreadsheet at upload.
 */
export function detectFileTypeFromSample(
  sample: Uint8Array,
): DetectedFileType | null {
  const detected = detectFileType(sample);
  if (detected) return detected;
  if (
    startsWith(sample, [0x50, 0x4b, 0x03, 0x04]) ||
    startsWith(sample, [0x50, 0x4b, 0x05, 0x06]) ||
    startsWith(sample, [0x50, 0x4b, 0x07, 0x08])
  ) {
    return { kind: "excel", mimeType: XLSX_MIME_TYPE };
  }
  return null;
}

/** File extension (including the dot) for a detected MIME type. */
export function extensionForMimeType(mimeType: string): string {
  switch (mimeType) {
    case PDF_MIME_TYPE:
      return ".pdf";
    case CSV_MIME_TYPE:
      return ".csv";
    case XLSX_MIME_TYPE:
      return ".xlsx";
    case XLS_MIME_TYPE:
      return ".xls";
    case "image/png":
      return ".png";
    case "image/jpeg":
      return ".jpg";
    case "image/gif":
      return ".gif";
    case "image/webp":
      return ".webp";
    case "image/bmp":
      return ".bmp";
    case "image/svg+xml":
      return ".svg";
    default:
      return ".bin";
  }
}
